/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getContentVisibilityAsync } from '../../../actions/extensions/DataActionExtension.g';

export interface IContentVisibility {
    RecordId?: number;
    RecordType?: string;
    VisibilityValue?: string;
    IsVisible?: boolean;
}

export interface IProductContentVisibilityData {
    visibility: IContentVisibility[];
}

class ProductContentVisibilityInput implements IActionInput {
    public productIds: number[];

    public catalogId: number;

    public constructor(productIds: number[], catalogId: number = 0) {
        this.productIds = productIds;
        this.catalogId = catalogId;
    }

    public getCacheKey = () => `ProductContentVisibility-${this.productIds.join(',') || 'none'}-${this.catalogId}`;

    public getCacheObjectType = () => 'ProductContentVisibility';

    public dataCacheType = (): CacheType => 'request';
}

const PRODUCT_ID_PATTERN = /(\d{4,})\.p(?:[?#/]|$)/i;

/**
 * Extracts a product record identifier from an authored link.
 * Several link shapes are supported because the destination URL is not always
 * resolved at the point the data action input is evaluated during server side rendering.
 * @param link - The authored link object.
 * @returns The product record identifier, or undefined when the link is not a product link.
 */
export const getProductIdFromLink = (link?: unknown): number | undefined => {
    if (!link) {
        return undefined;
    }

    const candidate = link as {
        destinationUrl?: string;
        linkUrl?: { destinationUrl?: string };
        productId?: number | string;
        recordId?: number | string;
    };

    const explicitId = Number(candidate.productId ?? candidate.recordId ?? Number.NaN);
    if (!Number.isNaN(explicitId) && explicitId > 0) {
        return explicitId;
    }

    const urls = [candidate.destinationUrl, candidate.linkUrl?.destinationUrl];
    for (const url of urls) {
        const match = url?.match(PRODUCT_ID_PATTERN);
        if (match) {
            const productId = Number(match[1]);
            if (!Number.isNaN(productId)) {
                return productId;
            }
        }
    }

    try {
        const match = JSON.stringify(candidate)?.match(PRODUCT_ID_PATTERN);
        if (match) {
            const productId = Number(match[1]);
            return Number.isNaN(productId) ? undefined : productId;
        }
    } catch {
        return undefined;
    }

    return undefined;
};

const createInput = (args: ICreateActionContext): IActionInput => {
    const config = args.config as {
        items?: {
            link?: unknown;
        }[];
    };

    const productIds = Array.from(
        new Set(
            (config?.items || [])
                .map(item => getProductIdFromLink(item.link))
                .filter((productId): productId is number => productId !== undefined)
        )
    );
    const catalogId = Number(args.requestContext.apiSettings.catalogId || 0);

    return new ProductContentVisibilityInput(productIds, catalogId);
};

async function action(input: ProductContentVisibilityInput, ctx: IActionContext): Promise<IProductContentVisibilityData> {
    if (!input.productIds.length) {
        return { visibility: [] };
    }

    const visibility = (await getContentVisibilityAsync(
        { callerContext: ctx, bypassCache: 'get' },
        input.productIds.join(','),
        input.catalogId
    )) as IContentVisibility[];

    return { visibility: visibility || [] };
}

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/moe-v2-list-arrow-right/get-product-content-visibility',
    action: <IAction<IProductContentVisibilityData>>action,
    input: createInput
});
